import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { faMessages, faEnvelope, faCommentSms, faPhone, faBook } from '@fortawesome/pro-regular-svg-icons';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { Box, createStyles, makeStyles, Theme, useMediaQuery } from '@material-ui/core';
import Button from 'components/UI/V2/Button/Button';
import { useToggleHubspotChat } from 'hooks';
import { HelpCenterPath } from 'routes/Paths';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        icon: {
            width: '24px',
            marginRight: '10px',
            color: '#555'
        },
        iconInfo: {
            width: '24px',
            marginRight: '10px',
            color: '#BAA997',
            fontSize: '22px',
            fontWeight: 900,
            paddingTop: '3px'
        },
        container: {
            width: '100%',
            maxWidth: '450px',
            overflow: 'hidden',
            textAlign: 'left'
        },
        header: {
            padding: '20px 20px 10px 20px'
        },
        title: {
            margin: 0
        },
        subtitle: {
            margin: '4px 0 15px 0',
            fontSize: '16px',
            fontWeight: 600
        },
        contactOptions: {
            padding: '10px 20px 0px 20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #D4D4D4'
        },
        helpCenterOptions: {
            marginTop: '15px',
            padding: '0px 20px 0px 20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #D4D4D4'
        },
        helpCenterLink: {
            'display': 'flex',
            'alignItems': 'center',
            'height': '56px',
            'fontSize': '14px',
            'fontWeight': 500,
            'textTransform': 'none',
            'color': '#222222',
            'textDecoration': 'none',
            '&:hover': {
                backgroundColor: 'transparent !important',
                color: '#C18733'
            }
        },
        contactItem: {
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #eee'
        },
        contactItemBottom: {
            display: 'flex',
            alignItems: 'center'
        },
        iconContainer: {
            width: '24px',
            marginRight: '15px'
        },
        contactText: {
            flexGrow: 1,
            fontSize: '14px',
            fontWeight: 500
        },
        emailLink: {
            paddingLeft: '6px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#222222'
        },
        contactButton: {
            'backgroundColor': 'white',
            'border': '2px solid #333',
            'borderRadius': '31px',
            'padding': '6px 15px',
            'fontSize': '14px',
            'fontWeight': 500,
            'color': '#222222',
            'marginBottom': '10px',
            'height': '25px',
            'display': 'flex',
            'justifyContent': 'space-between',
            'alignContent': 'center',
            'alignItems': 'center',
            '&:hover': {
                backgroundColor: 'transparent !important',
                color: '#C18733'
            }
        },
        contactButtonMobile: {
            paddingRight: '30px'
        },
        circleIcon: {
            width: '24px',
            height: '24px',
            backgroundColor: '#ddd',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        },
        noteContainer: {
            display: 'flex',
            alignItems: 'flex-start',
            marginTop: '15px',
            borderRadius: '12px',
            padding: '12px',
            border: '2px solid #BCB8AE'
        },
        warningText: {
            fontSize: '14px',
            fontWeight: 400,
            color: '#000000'
        }
    })
);

const HelpDescription = () => {
    const classes = useStyles();

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const { openChat } = useToggleHubspotChat();

    return (
        <Box className={classes.container}>
            <p className={classes.title}>Our Cuddles Support Team is here to help!</p>
            <p className={classes.subtitle}>Mon-Fri 8AM-8PM EST / Sat-Sun 10AM-7PM EST</p>

            <Box className={classes.contactOptions}>
                <Box className={classes.contactItem}>
                    <Box className={classes.iconContainer}>
                        <FontAwesomeIcon
                            icon={faMessages}
                            className={classes.icon}
                        />
                    </Box>
                    <p className={classes.contactText}>Chat with us online</p>
                    <Button
                        label="Contact us"
                        className={clsx(classes.contactButton, {
                            [classes.contactButtonMobile]: isMobile
                        })}
                        onClick={openChat}
                    />
                </Box>

                <Box className={classes.contactItem}>
                    <Box className={classes.iconContainer}>
                        <FontAwesomeIcon
                            icon={faEnvelope}
                            className={classes.icon}
                        />
                    </Box>
                    <p className={classes.contactText}>
                        Email us at
                        <a
                            className={classes.emailLink}
                            href="mailto:help@cuddlesapp.com"
                            target="_blank"
                            rel="noreferrer"
                        >
                            help@cuddlesapp.com
                        </a>
                    </p>
                </Box>

                <Box className={classes.contactItem}>
                    <Box className={classes.iconContainer}>
                        <FontAwesomeIcon
                            icon={faCommentSms}
                            className={classes.icon}
                        />
                    </Box>
                    <p className={classes.contactText}>
                        Text us at
                        <a
                            className={classes.emailLink}
                            href="sms:+178637776277"
                            target="_blank"
                            rel="noreferrer"
                        >
                            786-377-6277
                        </a>
                    </p>
                </Box>

                <Box className={classes.contactItem}>
                    <Box className={classes.contactItemBottom}>
                        <FontAwesomeIcon
                            icon={faPhone}
                            className={classes.icon}
                        />
                    </Box>
                    <p className={classes.contactText}>
                        Call us at
                        <a
                            className={classes.emailLink}
                            href="tel:+17865898249"
                            target="_blank"
                            rel="noreferrer"
                        >
                            786-589-8249
                        </a>
                    </p>
                </Box>
            </Box>

            <Box className={classes.helpCenterOptions}>
                <a
                    target="_blank"
                    rel="noreferrer"
                    href={HelpCenterPath}
                    className={classes.helpCenterLink}
                >
                    <Box className={classes.contactItemBottom}>
                        <FontAwesomeIcon
                            icon={faBook}
                            className={classes.icon}
                        />
                    </Box>
                    <p className={classes.contactText}>Visit our help center here</p>
                    <FontAwesomeIcon
                        icon={faCircleArrowRight}
                        className={classes.iconInfo}
                    />
                </a>
            </Box>

            <Box className={classes.noteContainer}>
                <FontAwesomeIcon
                    icon={faCircleInfo}
                    className={classes.iconInfo}
                />
                <Box className={classes.warningText}>
                    {"If you contact us outside of business hours, we'll follow up as soon as we're back."}
                </Box>
            </Box>
        </Box>
    );
};

export default HelpDescription;
